/**
 * Android broadcast profiles (ADR-091). A profile is DATA over the one generic broadcast provider: an intent
 * action to listen for and the names of the extras that carry the barcode. There is no per-manufacturer code
 * path anywhere. A manufacturer that needs a real SDK is a separate `VendorSdkProvider` adapter, never a
 * profile.
 */

export const CHARSETS = ['UTF-8', 'ISO-8859-1', 'US-ASCII', 'windows-1251', 'windows-1252'] as const;
export type Charset = (typeof CHARSETS)[number];

export type BroadcastProfile = {
  readonly id: string;
  readonly displayName: string;
  /** Intent action the scanner service broadcasts. */
  readonly action: string;
  /** Extra that carries the barcode (String, or byte[] decoded with `charset`). */
  readonly valueExtra: string;
  readonly symbologyExtra?: string;
  /** Extra that names the source/engine (diagnostics only). */
  readonly sourceExtra?: string;
  /** Only broadcasts sent from this package are accepted. Enforced on Android 14+ (the OS reports the sender). */
  readonly senderPackage?: string;
  readonly charset: Charset;
  readonly enabled: boolean;
  /** Lower first when several profiles are active. */
  readonly priority: number;
  readonly maxLength: number;
  /** Per-profile override of the same-provider duplicate window. */
  readonly duplicateWindowMs?: number;
};

export type PresetStatus = 'VERIFIED' | 'UNVERIFIED_TEMPLATE';

/**
 * A preset is a named, ready-made profile. VERIFIED means it was confirmed on real hardware and carries the
 * evidence. UNVERIFIED_TEMPLATE is structure and guidance only: it must not carry vendor action/extra strings
 * that nobody confirmed. The WMS default is OUR OWN convention that the scanner's Intent output is configured
 * to match, so it is not a claim about any vendor.
 */
export type ScannerPreset = {
  readonly id: string;
  readonly displayName: string;
  readonly status: PresetStatus;
  readonly profile: Omit<BroadcastProfile, 'id' | 'displayName' | 'enabled' | 'priority'>;
  /** Where to find the values on the device (shown to the person configuring it). */
  readonly howToConfigure: string;
  /** VERIFIED only: what it was verified on. */
  readonly verifiedOn?: { readonly model: string; readonly androidVersion: string; readonly date: string };
};

export const WMS_DEFAULT_ACTION = 'com.latynex.wms.scanner.SCAN';
export const WMS_DEFAULT_VALUE_EXTRA = 'barcode';
export const WMS_DEFAULT_SYMBOLOGY_EXTRA = 'symbology';

const BASE = { charset: 'UTF-8' as const, maxLength: 1024 };

export const SCANNER_PRESETS: readonly ScannerPreset[] = [
  {
    id: 'wms-default',
    displayName: 'LATYNEX default (set the scanner to this action)',
    status: 'UNVERIFIED_TEMPLATE',
    profile: { ...BASE, action: WMS_DEFAULT_ACTION, valueExtra: WMS_DEFAULT_VALUE_EXTRA, symbologyExtra: WMS_DEFAULT_SYMBOLOGY_EXTRA },
    howToConfigure:
      'In the scanner service settings choose Intent / Broadcast output and enter this action and these extra names. Works with any scanner service that lets you set the action and the data key.',
  },
  {
    id: 'scanner-service-template',
    displayName: 'Scanner service already configured (copy values)',
    status: 'UNVERIFIED_TEMPLATE',
    profile: { ...BASE, action: '', valueExtra: '' },
    howToConfigure:
      'Open the device scanner settings, find the Intent / Broadcast output, and copy its Action and the Extra (key) name of the barcode data into Custom.',
  },
];

/** Hardware-confirmed presets. Empty until a device is actually verified; entries must carry `verifiedOn`. */
export const VERIFIED_PRESET_IDS: readonly string[] = SCANNER_PRESETS.filter((p) => p.status === 'VERIFIED').map((p) => p.id);

const ID_RE = /^[a-z0-9][a-z0-9_.-]{0,39}$/;
// Action / extra names: dotted identifiers (the same shape Android itself uses).
const ACTION_RE = /^[A-Za-z_][A-Za-z0-9_.]{0,199}$/;
const EXTRA_RE = /^[A-Za-z_][A-Za-z0-9_.\-]{0,127}$/;
const PACKAGE_RE = /^[A-Za-z][A-Za-z0-9_]*(\.[A-Za-z][A-Za-z0-9_]*)+$/;
// System broadcast namespaces are not scanner output; accepting them would only invite confusion.
// Everything in the platform / Google / this app's own namespace is a system or app-internal broadcast, never scanner output.
export const FORBIDDEN_ACTION_PREFIXES: readonly string[] = ['android.', 'androidx.', 'com.android.', 'com.google.', 'com.wmscore.'];

export type ProfileValidation =
  | { readonly ok: true; readonly profile: BroadcastProfile }
  | { readonly ok: false; readonly errors: readonly string[] };

function str(v: unknown): string | undefined {
  return typeof v === 'string' ? v.trim() : undefined;
}

/** Strict: unknown fields are ignored, wrong types and malformed names are errors. Never throws. */
export function validateBroadcastProfile(input: unknown): ProfileValidation {
  const errors: string[] = [];
  if (typeof input !== 'object' || input === null) return { ok: false, errors: ['profile must be an object'] };
  const o = input as Record<string, unknown>;

  const id = str(o.id);
  if (id === undefined || !ID_RE.test(id)) errors.push('id: lowercase letters, digits, . _ - (max 40)');
  const displayName = str(o.displayName);
  if (displayName === undefined || displayName.length < 1 || displayName.length > 60) errors.push('displayName: 1-60 characters');
  const action = str(o.action);
  if (action === undefined || !ACTION_RE.test(action)) errors.push('action: dotted identifier, e.g. com.vendor.SCAN');
  else if (FORBIDDEN_ACTION_PREFIXES.some((p) => action.startsWith(p))) errors.push('action: system broadcast actions are not allowed');
  const valueExtra = str(o.valueExtra);
  if (valueExtra === undefined || !EXTRA_RE.test(valueExtra)) errors.push('valueExtra: extra key name');

  const optional = (key: 'symbologyExtra' | 'sourceExtra'): string | undefined => {
    const v = o[key];
    if (v === undefined || v === null || v === '') return undefined;
    const s = str(v);
    if (s === undefined || !EXTRA_RE.test(s)) {
      errors.push(`${key}: extra key name`);
      return undefined;
    }
    return s;
  };
  const symbologyExtra = optional('symbologyExtra');
  const sourceExtra = optional('sourceExtra');

  let senderPackage: string | undefined;
  if (o.senderPackage !== undefined && o.senderPackage !== null && o.senderPackage !== '') {
    const s = str(o.senderPackage);
    if (s === undefined || s.length > 200 || !PACKAGE_RE.test(s)) errors.push('senderPackage: Android package name');
    else senderPackage = s;
  }

  let charset: Charset = 'UTF-8';
  if (o.charset !== undefined) {
    if (typeof o.charset === 'string' && (CHARSETS as readonly string[]).includes(o.charset)) charset = o.charset as Charset;
    else errors.push(`charset: one of ${CHARSETS.join(', ')}`);
  }

  const enabled = o.enabled === undefined ? true : o.enabled === true;
  if (o.enabled !== undefined && typeof o.enabled !== 'boolean') errors.push('enabled: boolean');

  let priority = 50;
  if (o.priority !== undefined) {
    if (typeof o.priority === 'number' && Number.isInteger(o.priority) && o.priority >= 0 && o.priority <= 100) priority = o.priority;
    else errors.push('priority: integer 0-100');
  }

  let maxLength = 1024;
  if (o.maxLength !== undefined) {
    if (typeof o.maxLength === 'number' && Number.isInteger(o.maxLength) && o.maxLength >= 1 && o.maxLength <= 1024) maxLength = o.maxLength;
    else errors.push('maxLength: integer 1-1024');
  }

  let duplicateWindowMs: number | undefined;
  if (o.duplicateWindowMs !== undefined) {
    if (typeof o.duplicateWindowMs === 'number' && Number.isInteger(o.duplicateWindowMs) && o.duplicateWindowMs >= 0 && o.duplicateWindowMs <= 2000) {
      duplicateWindowMs = o.duplicateWindowMs;
    } else errors.push('duplicateWindowMs: integer 0-2000');
  }

  if (errors.length > 0) return { ok: false, errors };
  return {
    ok: true,
    profile: {
      id: id!,
      displayName: displayName!,
      action: action!,
      valueExtra: valueExtra!,
      ...(symbologyExtra !== undefined ? { symbologyExtra } : {}),
      ...(sourceExtra !== undefined ? { sourceExtra } : {}),
      ...(senderPackage !== undefined ? { senderPackage } : {}),
      charset,
      enabled,
      priority,
      maxLength,
      ...(duplicateWindowMs !== undefined ? { duplicateWindowMs } : {}),
    },
  };
}

/** Builds the profile a preset describes (still validated: a template with blank action is not usable). */
export function profileFromPreset(preset: ScannerPreset, overrides: Partial<Pick<BroadcastProfile, 'enabled' | 'priority'>> = {}): ProfileValidation {
  return validateBroadcastProfile({
    ...preset.profile,
    id: preset.id,
    displayName: preset.displayName,
    enabled: overrides.enabled ?? true,
    priority: overrides.priority ?? 50,
  });
}
